import React from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, ToastServiceProviderAccreditation } from '@philips-emr/hpms-provider-accreditation-commons';
import { FetchAPI, FieldAPI, LegendsAPI, Link, PanelAPI } from '@philips-emr/tws-components';
import Controller, { OnAfterFetch, OnCancel, OnChange, OnClose, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';

import { NegotiationServicesAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { Item } from './negotiation-procedures-controller';

const SERVICE = 'S';

interface Service {
  dsItem: string,
  edit: string,
  vlService: number,
  nrSeqItemProposal: number,
  vlCpService: number,
  ieValueCounterProposal: string,
}

@Controller({ fragment: FragmentNames.NEGOTIATION_SERVICES })
class NegotiationServicesController extends BaseController {

  @Inject(Aliases.LEGENDS)
  private legends: LegendsAPI;
  @Inject(Aliases.PANEL_MODAL_SERVICE)
  private panelModal: PanelAPI;
  @Inject(Aliases.FETCH_SERVICES)
  private fetchServices: FetchAPI;
  @Inject(Aliases.FIELD_ORIGIN)
  private fieldOrigin: FieldAPI;

  @OnMount(Aliases.FETCH_SERVICES)
  onMountFetch(event: MountEvent): void {
    const api = event.target as FetchAPI;
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqNegotiation, nrSeqProposal, 'itemType': SERVICE });
    api.setParameters({ nrSeqNegotiation, nrSeqProposal, 'itemType': SERVICE });
    api.forceFetch();
  }

  @OnAfterFetch(Aliases.FETCH_SERVICES)
  onAfterFetchMaterials(event: FetchEvent): void {
    const data = event.data as Service[];
    if (!data || data.length === 0) {
      this.legends.setShowExpression('');
      this.legends.setHideExpression('');
    }
  }

  @OnMount(Aliases.FIELD_COUNTER_OFFER_VALUE)
  onMountFieldCounterOfferValue(event: MountEvent): void {
    const api = event.target as FieldAPI;
    const data = api.getData();
    if (data) {
      const { vlCpService } = data as unknown as Service;
      if (vlCpService > 0) {
        api.setVisible(true);
      }
    }
  }

  @OnDynamicContentRender(Aliases.CARD_SERVICE)
  onRenderLinkEdit(event: RenderEvent): void {
    const { render, data } = event;
    const { edit, nrSeqItemProposal } = data as Item;
    if (edit == 'S') {
      render(() =>
        <Link
          className="link-negotiation-materials__button_align"
          onClick={() => this.onOpenEditProposal(nrSeqItemProposal)}
          value={MessageService.translate('pa.change-value')}
        />
      );
    }
  }

  @OnClose(Aliases.MODAL_SERVICE)
  onCloseModal(): void {
    this.panelModal.setVisible(false);
  }

  @OnCancel(Aliases.FORM_SERVICE)
  onCancelCounterOffer(): void {
    this.panelModal.setVisible(false);
  }

  @OnSubmit(Aliases.FORM_SERVICE)
  onSubmitMaterial(event: SubmitEvent): void {
    const body = { ...event.record, 'itemType': SERVICE };
    const { nrSeqProposal, nrSeqNegotiation } = this.historyService.getCurrentRouteParams();

    HttpService.post(Endpoint.POST.EDIT_NEGOTIATION_PROPOSAL_ITEM, { nrSeqProposal, nrSeqNegotiation }, { body })
      .then(() => {
        this.fetchServices.forceFetch();
        this.panelModal.setVisible(false);
        ToastServiceProviderAccreditation.successBubble(MessageService.translate('pa.request-success'));
      }).catch(() => {
        ToastServiceProviderAccreditation.error(MessageService.translate('pa.error-process-request'));
      });
  }

  @OnChange(Aliases.FIELD_CODE)
  onChangeFieldCode(event: ChangeEvent<{ cdProcedure: string }>): void {
    const cdProcedure = event.value;
    if (cdProcedure) {
      this.fieldOrigin.setValidators([{ name: 'required', options: {} }] as Validator[]);
    } else {
      this.fieldOrigin.setValidators([{}] as Validator[]);
    }
  }

  onOpenEditProposal(nrSeqItemProposal: number): void {
    this.context.set({ nrSeqItemProposal });
    this.panelModal.setVisible(true);
  }

}

export default NegotiationServicesController;
